import React, { useEffect, useRef, useState } from "react";
import {
  Download,
  FileText,
  Menu,
  Paperclip,
  Printer,
} from "lucide-react";

import { AlertV4, ButtonV4, ModalV4, PaginationV4, SelectV4 } from "../common/ui";
import { errorTextOf } from "../common/errorTextV4";
import { isButtonLoading } from "../common/sequence/sequenceStateV4";

import { downloadScanPages, printScanPages, readScanFiles } from "./documentFileV4";
import { printMedCert } from "./medCertPdfV4";
import {
  cancelPrintTask,
  checkPrintTask,
  downloadPrintFile,
  loadPrintEncounters,
  previewPrintForm,
  printPrintForm,
  resolvePrintEncounter,
  type PrintEncounterOption,
  type PrintFormContext,
  type PrintListController,
  type PrintResult,
} from "./printListV4";
import {
  asNumber,
  categoryIdsWithin,
  filterClinicForms,
  filterScannedDocs,
  formatNextDocName,
  isCertCopy,
  isPrintableCertCode,
  isValidThaiDate,
  medCertIssueProblem,
  normalizeDocumentV4Slice,
  todayThaiDate,
  type ClinicFormItem,
  type DocumentIds,
  type DocumentRunSequenceProp,
  type DocumentV4Slice,
  type MedicalCertItem,
  type ScanPage,
  type ScannedDocFilter,
  type ScannedDocItem,
} from "./documentModelV4";
import type { MockEncounter, MockPatient } from "./mockPatientV4";

const DOC_PAGE_SIZE = 10;
const PRINT_TASK_POLL_MS = 5000;
const PDF_ZOOM_STEP = 25;

type ScanFilterForm = {
  categoryId: string;
  typeId: string;
  query: string;
  scanFrom: string;
  scanTo: string;
  docFrom: string;
  docTo: string;
  expiryFrom: string;
  expiryTo: string;
  ownerDivisionId: string;
  scanDivisionId: string;
  doctorId: string;
  onlyEncounter: boolean;
};

const EMPTY_SCAN_FILTER: ScanFilterForm = {
  categoryId: "all",
  typeId: "all",
  query: "",
  scanFrom: "",
  scanTo: "",
  docFrom: "",
  docTo: "",
  expiryFrom: "",
  expiryTo: "",
  ownerDivisionId: "all",
  scanDivisionId: "all",
  doctorId: "all",
  onlyEncounter: false,
};

const selectedId = (value: string): number | undefined =>
  value === "all" ? undefined : asNumber(value);

export type CardDocumentV4Props = {
  patient?: MockPatient | any;
  encounter?:
    | MockEncounter
    | {
        id?: unknown;
        number?: string;
        hn?: string;
        patient_id?: unknown;
        doctor_name?: string;
      };
  /** V3 ให้แพทย์เท่านั้นที่ออกใบรับรองได้ */
  canIssueCert?: boolean;
  /** slice ของ DocumentV4 sequence เมื่อโหลดข้อมูลจริงจาก API (live mode) */
  docs?: Partial<DocumentV4Slice> | null;
  ids?: DocumentIds;
  runSequence?: DocumentRunSequenceProp;
  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  initialDocMode?: "document" | "medcert";
  divisionOptions?: any[];
  /** controller เดิมของ V3 ที่แบบฟอร์ม Print list ใช้อ่านข้อมูลและเรียก print API */
  printListController?: PrintListController;
  masterOptions?: Record<string, any>;
  masterData?: Record<string, any>;
  selectedDivision?: Record<string, any>;
  onSelectMenu?: (menu: any) => void;
  onBackToQueue?: () => void;
};

export const CardDocumentV4: React.FC<CardDocumentV4Props> = (props) => {
  const {
    patient,
    encounter,
    docs,
    ids = {},
    runSequence,
    buttonLoadCheck = {},
    errorMessage = {},
    initialDocMode = "document",
    divisionOptions = [],
    canIssueCert = true,
    printListController,
    masterOptions,
    masterData,
    selectedDivision,
    onSelectMenu,
  } = props;

  /* ห้ามใส่ค่าสมมติของผู้ป่วยหรือแพทย์ เอกสารต้องแสดงเฉพาะข้อมูลจริงของคนที่เปิดอยู่ */
  const currentPatientId = asNumber(ids.patientId ?? (encounter as any)?.patient_id);
  const currentEncounterId = asNumber(ids.encounterId ?? (encounter as any)?.id);
  const loadedPatientId = asNumber(patient?.id);
  const isOtherPatient =
    currentPatientId !== undefined &&
    loadedPatientId !== undefined &&
    currentPatientId !== loadedPatientId;

  const encNumber = String(encounter?.number || "");
  const patientHn = String(
    ids.patientHn || (encounter as any)?.hn || (isOtherPatient ? "" : patient?.hn) || "",
  );
  const patientName = isOtherPatient ? "" : String(patient?.full_name || "");

  // Live slice from sequence
  const slice: DocumentV4Slice = normalizeDocumentV4Slice(docs);

  // Mode: "document" (Print forms + Scanned docs) vs "medcert" (Medical certificates)
  const [docMode, setDocMode] = useState<"document" | "medcert">(initialDocMode);

  // Sub-tabs under Document: "print" vs "scan"
  const [docSubTab, setDocSubTab] = useState<"print" | "scan">("print");

  // Toast / Status banner message
  const [message, setMessage] = useState<string>("");

  // Loading & Error states from central sequence state
  const isSavingDoc = isButtonLoading(buttonLoadCheck, {
    sequence: "DocumentV4",
    card: "CardDocumentV4",
    action: "SAVE_SCANNED_DOC",
  });
  const isIssuingCert = isButtonLoading(buttonLoadCheck, {
    sequence: "DocumentV4",
    card: "CardDocumentV4",
    action: "ISSUE_MED_CERT",
  });
  const isDeletingDoc = isButtonLoading(buttonLoadCheck, {
    sequence: "DocumentV4",
    card: "CardDocumentV4",
    action: "DELETE_SCANNED_DOC",
  });
  const isLoadingCert = isButtonLoading(buttonLoadCheck, {
    sequence: "DocumentV4",
    card: "CardDocumentV4",
    action: "GET_MED_CERT",
  });
  const isReprintingCert = isButtonLoading(buttonLoadCheck, {
    sequence: "DocumentV4",
    card: "CardDocumentV4",
    action: "REPRINT_MED_CERT",
  });
  const rawError = errorMessage?.CardDocumentV4;
  const apiError = rawError ? errorTextOf(rawError) : null;

  /** requestId ของ action ที่รอผล ผลของคำขออื่นไม่นำมาแสดง */
  const pendingRequestRef = useRef<string | null>(null);
  const nextRequestId = (action: string): string => {
    const requestId = `${action}_${Date.now()}`;
    pendingRequestRef.current = requestId;
    return requestId;
  };

  // ==========================================
  // TAB 1: Print List (แบบฟอร์มคลินิก) State
  // ==========================================

  const [formSearch, setFormSearch] = useState<string>("");

  const formsSource = slice.forms;
  const filteredForms: ClinicFormItem[] = filterClinicForms(formsSource, formSearch);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const currentForm = formsSource.find((form) => form.id === selectedFormId);
  const currentFormName = currentForm?.name ?? "";

  const [printFileUrl, setPrintFileUrl] = useState<string>("");
  const [printTask, setPrintTask] = useState<{ taskId: string; progress?: string } | null>(null);
  const [isPreparingForm, setIsPreparingForm] = useState<boolean>(false);
  const [isPrintingForm, setIsPrintingForm] = useState<boolean>(false);
  const [printProblem, setPrintProblem] = useState<string>("");
  const [printEncounters, setPrintEncounters] = useState<PrintEncounterOption[]>([]);
  const [printEncounter, setPrintEncounter] = useState<{
    encounterId: number;
    encounterNumber: string;
    doctorName: string;
    emrId?: number;
  } | null>(null);
  /** คำขอล่าสุดเท่านั้นที่เขียนผลได้ กันเลือกฟอร์มหรือ Encounter เร็ว ๆ แล้วเห็นไฟล์ผิดใบ */
  const printRequestRef = useRef<number>(0);
  const printBlobRef = useRef<string>("");

  const printTargetEncounterId = printEncounter?.encounterId ?? currentEncounterId;
  const printContext: PrintFormContext | null =
    printListController && patientName && printTargetEncounterId
      ? {
          controller: printListController,
          patientInfo: {
            ...patient,
            encounter_id: printTargetEncounterId,
            encounter_no: printEncounter?.encounterNumber ?? encNumber,
            doctor_name: printEncounter?.doctorName ?? (encounter as any)?.doctor_name,
          },
          patientId: currentPatientId,
          encounterId: printTargetEncounterId,
          encounterNumber: printEncounter?.encounterNumber ?? encNumber,
          emrId: printEncounter === null ? asNumber(ids.emrId ?? slice.emrId) : printEncounter.emrId,
          masterOptions,
          masterData,
          selectedDivision,
        }
      : null;
  const printBlockedReason = !printListController
    ? "ยังเชื่อมต่อระบบพิมพ์ไม่ได้"
    : !patientName
      ? "ยังไม่มีข้อมูลผู้ป่วยสำหรับใส่ในแบบฟอร์ม"
      : !printTargetEncounterId
        ? "ยังไม่มีข้อมูล Encounter"
        : "";

  const replacePrintFile = (url: string): void => {
    if (printBlobRef.current && printBlobRef.current !== url) {
      URL.revokeObjectURL(printBlobRef.current);
    }
    printBlobRef.current = url.startsWith("blob:") ? url : "";
    setPrintFileUrl(url);
  };

  const applyPrintResult = (result: PrintResult, printing: boolean): void => {
    setPrintTask(result.kind === "task" ? { taskId: result.taskId, progress: result.progress } : null);
    if (result.kind === "file") {
      replacePrintFile(result.url);
      if (printing) setMessage(`เปิดหน้าต่างพิมพ์แล้ว: ${currentFormName}`);
    } else if (result.kind === "printed") {
      setMessage(`ส่งพิมพ์แล้ว: ${currentFormName}`);
    } else if (result.kind === "warning" || result.kind === "error") {
      setPrintProblem(result.message);
    }
  };

  const runPreview = async (form: ClinicFormItem, context: PrintFormContext): Promise<void> => {
    const request = printRequestRef.current + 1;
    printRequestRef.current = request;
    setPrintProblem("");
    setPrintTask(null);
    replacePrintFile("");
    setIsPreparingForm(true);
    const result = await previewPrintForm(form, context);
    if (printRequestRef.current !== request) return;
    setIsPreparingForm(false);
    applyPrintResult(result, false);
  };

  const handleSelectForm = (form: ClinicFormItem): void => {
    setSelectedFormId(form.id);
    setMessage("");
    if (printContext) void runPreview(form, printContext);
  };

  const handlePrintForm = async (): Promise<void> => {
    if (!currentForm || !printContext) return;
    const request = printRequestRef.current;
    setMessage("");
    setPrintProblem("");
    setIsPrintingForm(true);
    const result = await printPrintForm(currentForm, printContext, printFileUrl || undefined);
    setIsPrintingForm(false);
    if (printRequestRef.current === request) applyPrintResult(result, true);
  };

  const handleSelectPrintEncounter = async (value: string): Promise<void> => {
    const encounterId = asNumber(value);
    if (encounterId === undefined || !printListController) return;
    const request = printRequestRef.current + 1;
    printRequestRef.current = request;
    setPrintProblem("");
    setPrintTask(null);
    setSelectedFormId(null);
    replacePrintFile("");
    setIsPreparingForm(false);
    try {
      const resolved = await resolvePrintEncounter(printListController, encounterId);
      if (printRequestRef.current === request) setPrintEncounter(resolved);
    } catch {
      if (printRequestRef.current === request) setPrintProblem("อ่านข้อมูล Encounter ไม่สำเร็จ");
    }
  };

  const handleCancelPrintTask = (): void => {
    if (printTask === null || !printListController) return;
    printRequestRef.current += 1;
    void cancelPrintTask(printListController, printTask.taskId);
    setPrintTask(null);
    setPrintProblem("ยกเลิกการสร้างเอกสารแล้ว");
  };

  useEffect(() => {
    if (!printListController || !patientHn) return undefined;
    let active = true;
    loadPrintEncounters(printListController, patientHn)
      .then((options) => {
        if (active) setPrintEncounters(options);
      })
      .catch(() => {
        if (active) setPrintEncounters([]);
      });
    return () => {
      active = false;
    };
  }, [printListController, patientHn]);

  const printTaskId = printTask?.taskId;
  useEffect(() => {
    if (!printTaskId || !printListController) return undefined;
    const request = printRequestRef.current;
    const timer = window.setInterval(async () => {
      const result = await checkPrintTask(printListController, printTaskId);
      if (printRequestRef.current !== request) return;
      applyPrintResult(result, false);
    }, PRINT_TASK_POLL_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printTaskId, printListController]);

  useEffect(
    () => () => {
      printRequestRef.current += 1;
      if (printBlobRef.current) URL.revokeObjectURL(printBlobRef.current);
    },
    [],
  );

  // ==========================================
  // TAB 2: Scanned Documents (เอกสารสแกน) State
  // ==========================================
  const [scanTypeId, setScanTypeId] = useState<string>("");
  const [scanNote, setScanNote] = useState<string>("");
  const [stagedFiles, setStagedFiles] = useState<ScanPage[]>([]);
  const [isReadingFiles, setIsReadingFiles] = useState<boolean>(false);
  const [fileProblem, setFileProblem] = useState<string>("");
  /** ส่งซ้ำเฉพาะหน้าที่ล้ม ต้องเข้าเอกสารชุดเดิม จึงล็อกประเภทและชื่อไว้ */
  const [retryDoc, setRetryDoc] = useState<{ documentCode: string; docName: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Scanned docs list & filters
  const [filterForm, setFilterForm] = useState<ScanFilterForm>(EMPTY_SCAN_FILTER);
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(false);
  const [docPage, setDocPage] = useState<number>(1);
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
  const [viewFileIdx, setViewFileIdx] = useState<number>(0);
  const [viewZoom, setViewZoom] = useState<number>(100);
  const [failedImage, setFailedImage] = useState<string>("");
  const [isPreparingOutput, setIsPreparingOutput] = useState<boolean>(false);

  // Cancel document modal
  const [cancelTarget, setCancelTarget] = useState<ScannedDocItem | null>(null);
  const [cancelUsername, setCancelUsername] = useState<string>("");
  const [cancelPassword, setCancelPassword] = useState<string>("");
  const [cancelNote, setCancelNote] = useState<string>("");

  const typeOptions = slice.documentTypes.map((type) => ({
    value: String(type.id),
    text: type.name,
  }));
  const categoryOptions = slice.documentCategories.map((category) => ({
    value: String(category.id),
    text: category.name,
  }));
  const divisionSelectOptions = divisionOptions
    .filter((option: any) => asNumber(option?.id ?? option?.value) !== undefined)
    .map((option: any) => ({
      value: String(option.id ?? option.value),
      text: String(option.name ?? option.text ?? option.label ?? option.code ?? ""),
    }));
  const doctorOptions = Array.from(
    new Map(
      slice.scannedDocs
        .filter((doc) => doc.doctorId !== undefined && doc.doctorName)
        .map((doc) => [String(doc.doctorId), doc.doctorName]),
    ),
  ).map(([value, text]) => ({ value, text }));

  const filterCategoryId = selectedId(filterForm.categoryId);
  const filterCategoryIds =
    filterCategoryId === undefined
      ? undefined
      : categoryIdsWithin(slice.documentCategories, filterCategoryId);
  const filterTypeOptions = slice.documentTypes
    .filter(
      (type) =>
        filterCategoryIds === undefined ||
        (type.categoryId !== undefined && filterCategoryIds.includes(type.categoryId)),
    )
    .map((type) => ({ value: String(type.id), text: type.name }));

  const scanFilter: ScannedDocFilter = {
    typeId: selectedId(filterForm.typeId),
    categoryIds: filterCategoryIds,
    query: filterForm.query,
    scanFrom: filterForm.scanFrom,
    scanTo: filterForm.scanTo,
    docFrom: filterForm.docFrom,
    docTo: filterForm.docTo,
    expiryFrom: filterForm.expiryFrom,
    expiryTo: filterForm.expiryTo,
    ownerDivisionId: selectedId(filterForm.ownerDivisionId),
    scanDivisionId: selectedId(filterForm.scanDivisionId),
    doctorId: selectedId(filterForm.doctorId),
    encounterId: filterForm.onlyEncounter ? currentEncounterId : undefined,
  };
  const hasDateProblem = [
    filterForm.scanFrom,
    filterForm.scanTo,
    filterForm.docFrom,
    filterForm.docTo,
    filterForm.expiryFrom,
    filterForm.expiryTo,
  ].some((value) => !isValidThaiDate(value));

  const filteredDocs = filterScannedDocs(slice.scannedDocs, scanFilter);
  const docPageCount = Math.max(1, Math.ceil(filteredDocs.length / DOC_PAGE_SIZE));
  const currentDocPage = Math.min(docPage, docPageCount);
  const visibleDocs = filteredDocs.slice(
    (currentDocPage - 1) * DOC_PAGE_SIZE,
    currentDocPage * DOC_PAGE_SIZE,
  );

  const updateFilter = (patch: Partial<ScanFilterForm>): void => {
    setFilterForm((current) => ({ ...current, ...patch }));
    setDocPage(1);
  };

  useEffect(() => {
    if (filteredDocs.length > 0) {
      if (selectedDocId === null || !filteredDocs.some((d) => d.id === selectedDocId)) {
        setSelectedDocId(filteredDocs[0].id);
        setViewFileIdx(0);
      }
    } else {
      setSelectedDocId(null);
    }
  }, [filteredDocs, selectedDocId]);

  const selectedDoc =
    (selectedDocId !== null ? filteredDocs.find((d) => d.id === selectedDocId) : undefined) ||
    filteredDocs[0];
  const activeFileIdx = selectedDoc
    ? Math.max(0, Math.min(viewFileIdx, selectedDoc.files.length - 1))
    : 0;
  const activeFile = selectedDoc?.files[activeFileIdx] ?? "";

  const handleNativeFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (files.length === 0) return;

    setFileProblem("");
    setIsReadingFiles(true);
    try {
      const { pages, rejected } = await readScanFiles(files);
      setStagedFiles((prev) => [...prev, ...pages]);
      if (rejected.length > 0) {
        setFileProblem(
          `ใช้ไฟล์เหล่านี้ไม่ได้ (รองรับ JPG และ PDF ไม่เกินหน้าละ 8 MB): ${rejected.join(", ")}`,
        );
      }
    } finally {
      setIsReadingFiles(false);
    }
  };

  const handleRemoveStaged = (index: number) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearStaged = () => {
    setStagedFiles([]);
    setRetryDoc(null);
    setFileProblem("");
  };

  const handleSaveScannedDoc = () => {
    if (stagedFiles.length === 0 || !scanTypeId) return;
    const typeName = slice.documentTypes.find((t) => String(t.id) === scanTypeId)?.name ?? "";
    const nextSeq = Math.max(...slice.scannedDocs.map((d) => d.id), 0) + 1;
    const docName = retryDoc?.docName ?? formatNextDocName(typeName, scanNote, patientHn, nextSeq);

    setMessage("");
    runSequence?.({
      sequence: "DocumentV4",
      action: "SAVE_SCANNED_DOC",
      card: "CardDocumentV4",
      requestId: nextRequestId("SAVE_SCANNED_DOC"),
      patientId: currentPatientId,
      encounterId: currentEncounterId,
      patientHn,
      docName,
      typeId: asNumber(scanTypeId),
      documentCode: retryDoc?.documentCode,
      pages: [...stagedFiles],
    });
  };

  const closeCancelModal = () => {
    setCancelTarget(null);
    setCancelUsername("");
    setCancelPassword("");
    setCancelNote("");
  };

  const handleCancelDoc = () => {
    if (cancelTarget === null) return;
    setMessage("");
    runSequence?.({
      sequence: "DocumentV4",
      action: "DELETE_SCANNED_DOC",
      card: "CardDocumentV4",
      requestId: nextRequestId("DELETE_SCANNED_DOC"),
      docId: cancelTarget.id,
      rowIds: cancelTarget.rowIds,
      username: cancelUsername.trim(),
      password: cancelPassword,
      cancelNote: cancelNote.trim(),
      label: cancelTarget.name,
    });
    setCancelPassword("");
  };

  const handleDocOutput = async (kind: "print" | "download") => {
    if (!selectedDoc || selectedDoc.files.length === 0) return;
    setFileProblem("");
    setIsPreparingOutput(true);
    try {
      if (kind === "print") await printScanPages(selectedDoc.files);
      else await downloadScanPages(selectedDoc.files, selectedDoc.name);
    } catch {
      setFileProblem(`เปิดไฟล์เอกสารไม่สำเร็จ: ${selectedDoc.name}`);
    } finally {
      setIsPreparingOutput(false);
    }
  };

  // ==========================================
  // TAB 3: Medical Certificate (ใบรับรองแพทย์) State
  // ==========================================
  const issuableCertTypes = slice.medCertTypes.filter((type) => isPrintableCertCode(type.code));
  const [certTypeCode, setCertTypeCode] = useState<string>("");
  const selectedCertTypeCode = certTypeCode || issuableCertTypes[0]?.code || "";
  const [certFor, setCertFor] = useState<string>("");
  const [certRest, setCertRest] = useState<boolean>(false);
  const [certFromDate, setCertFromDate] = useState<string>(() => todayThaiDate());
  const [certDays, setCertDays] = useState<string>("");
  const [certOpinion, setCertOpinion] = useState<string>("");
  const [certPrintProblem, setCertPrintProblem] = useState<string>("");

  /* Dx อ่านอย่างเดียวจาก Principal ของ EMR ตาม #86627 */
  const certDx = slice.diagnosisText;
  const certDxDisplay =
    certDx ||
    (slice.diagnosisStatus === "failed"
      ? "อ่านการวินิจฉัยไม่สำเร็จ"
      : slice.diagnosisStatus === "idle"
        ? ""
        : "ยังไม่ได้ลงวินิจฉัย");
  const encounterType = slice.encounterType;
  const currentDoctorId = asNumber(ids.doctorId ?? slice.doctorId);
  const certProblem = medCertIssueProblem({
    canIssue: canIssueCert,
    encounterId: currentEncounterId,
    encounterType,
    patientName,
    doctorId: currentDoctorId,
    typeCode: selectedCertTypeCode,
    dx: certDx,
    rest: certRest,
    fromDate: certFromDate,
    days: certDays,
  });

  // Preview modal for Medical Certificate
  const [previewCert, setPreviewCert] = useState<MedicalCertItem | null>(null);
  const previewDetail =
    previewCert !== null && slice.certDetail?.id === previewCert.id ? slice.certDetail : null;
  const previewData = previewDetail?.data ?? {};

  const handleOpenCert = (cert: MedicalCertItem) => {
    setCertPrintProblem("");
    setPreviewCert(cert);
    runSequence?.({
      sequence: "DocumentV4",
      action: "GET_MED_CERT",
      card: "CardDocumentV4",
      certId: cert.id,
      formCode: cert.formCode,
      formName: cert.type,
    });
  };

  const handleReprintCert = () => {
    setCertPrintProblem("");
    runSequence?.({
      sequence: "DocumentV4",
      action: "REPRINT_MED_CERT",
      card: "CardDocumentV4",
      requestId: nextRequestId("REPRINT_MED_CERT"),
    });
  };

  const printCertDetail = async () => {
    const detail = slice.certDetail;
    if (detail === null) return;
    try {
      await printMedCert(detail.formCode, detail.data);
    } catch {
      setCertPrintProblem(`บันทึกแล้ว แต่เปิดพิมพ์ใบรับรองแพทย์เลขที่ ${detail.id} ไม่สำเร็จ`);
    }
  };

  const handleIssueCert = () => {
    /* แบบฟอร์มของ V3 ไม่มีช่องผู้รับเอกสาร จึงพิมพ์รวมในความเห็นแพทย์ */
    const comment = [certOpinion.trim(), certFor.trim() ? `ใช้เพื่อ / ยื่นต่อ: ${certFor.trim()}` : ""]
      .filter(Boolean)
      .join("\n");

    setMessage("");
    setCertPrintProblem("");
    runSequence?.({
      sequence: "DocumentV4",
      action: "ISSUE_MED_CERT",
      card: "CardDocumentV4",
      requestId: nextRequestId("ISSUE_MED_CERT"),
      encounterId: currentEncounterId,
      encounterNumber: encNumber,
      encounterType,
      doctorId: currentDoctorId,
      typeCode: selectedCertTypeCode,
      patient: {
        full_name: patientName,
        hn: patientHn,
        citizen_no: patient?.citizen_no,
        citizen_passport: patient?.citizen_passport,
        birthdate: patient?.birthdate,
      },
      comment,
      rest: certRest,
      fromDate: certFromDate,
      days: certRest ? Number(certDays.trim()) : 0,
    });
  };

  /* แสดงสำเร็จและล้างฟอร์มหลังได้ผลจาก API ของคำขอนี้เท่านั้น ถ้าล้มข้อมูลที่กรอกยังอยู่ */
  const actionResult = slice.actionResult;
  useEffect(() => {
    if (actionResult === null || actionResult.requestId !== pendingRequestRef.current) return;
    pendingRequestRef.current = null;
    if (!actionResult.ok) {
      /* หน้าที่สำเร็จแล้วเอาออก เหลือเฉพาะหน้าที่ต้องส่งซ้ำเข้าชุดเดิม */
      const failedPages = actionResult.failedPages;
      if (actionResult.action === "SAVE_SCANNED_DOC" && failedPages && actionResult.documentCode) {
        setStagedFiles((prev) =>
          prev
            .map((page, index) => ({ ...page, pageNo: page.pageNo ?? index + 1 }))
            .filter((page) => failedPages.includes(page.pageNo)),
        );
        setRetryDoc({
          documentCode: actionResult.documentCode,
          docName: actionResult.label ?? "",
        });
      }
      return;
    }

    const reloadNote = actionResult.reloadFailed ? " (โหลดรายการใหม่ไม่สำเร็จ)" : "";
    if (actionResult.action === "SAVE_SCANNED_DOC") {
      setViewFileIdx(0);
      setStagedFiles([]);
      setRetryDoc(null);
      setScanNote("");
      setMessage(`บันทึกเอกสารแล้ว: ${actionResult.label ?? ""}${reloadNote}`);
    } else if (actionResult.action === "DELETE_SCANNED_DOC") {
      closeCancelModal();
      setMessage(`ยกเลิกเอกสารแล้ว: ${actionResult.label ?? ""}`);
    } else if (actionResult.action === "ISSUE_MED_CERT") {
      setCertFor("");
      setCertOpinion("");
      setCertRest(false);
      setCertDays("");
      setMessage(
        `ออกใบรับรองแพทย์แล้ว${actionResult.label ? `: เลขที่ ${actionResult.label}` : ""}${reloadNote}`,
      );
      void printCertDetail();
    } else if (actionResult.action === "REPRINT_MED_CERT") {
      void printCertDetail();
    }
  }, [actionResult]);

  return (
    <>
      {/* Top Group Tabs matching V4 Header navigation */}
      <div className="hv4-grp-tabs">
        {docMode === "document" ? (
          <span className="hv4-grp-tab hv4-grp-tab-on" aria-current="page">
            Document
          </span>
        ) : (
          <button
            type="button"
            className="hv4-grp-tab"
            onClick={() => {
              setDocMode("document");
              setMessage("");
            }}
          >
            Document
          </button>
        )}
        {docMode === "medcert" ? (
          <span className="hv4-grp-tab hv4-grp-tab-on" aria-current="page">
            ใบรับรองแพทย์
          </span>
        ) : (
          <button
            type="button"
            className="hv4-grp-tab"
            onClick={() => {
              setDocMode("medcert");
              setMessage("");
            }}
          >
            ใบรับรองแพทย์
          </button>
        )}
      </div>

      {/* Main Header */}
      <div className="hv4-page-head" style={{ alignItems: "baseline", gap: "4px 16px" }}>
        <h1 className="hv4-h1">
          {docMode === "document" ? "Document" : "ใบรับรองแพทย์"}
        </h1>
        <span style={{ fontSize: "14px", color: "var(--hv4-ink-off)" }}>
          {docMode === "document"
            ? "แบบฟอร์มของคลินิกและเอกสารสแกนของผู้ป่วย"
            : "ออกใบรับรอง · ประวัติการออก · วินิจฉัยดึงจากหน้าตรวจรักษา"}
        </span>
      </div>

      {/* API Error banner */}
      {apiError && (
        <AlertV4 tone="danger">
          {apiError}
        </AlertV4>
      )}

      {/* Status banner */}
      {message && (
        <AlertV4
          tone="info"
          onClose={() => setMessage("")}
        >
          {message}
        </AlertV4>
      )}



      {/* ========================================================================= */}
      {/* MODE 1: DOCUMENT SCREEN (Print list + Scanned docs) */}
      {/* ========================================================================= */}
      {docMode === "document" && (
        <>
          {/* Sub Tab Navigation */}
          <div role="tablist" className="hv4-doc-tabs">
            <button
              role="tab"
              type="button"
              aria-selected={docSubTab === "print"}
              className={`hv4-doc-tab ${
                docSubTab === "print" ? "hv4-doc-tab--active" : ""
              }`}
              onClick={() => {
                setDocSubTab("print");
                setMessage("");
              }}
            >
              Print list (แบบฟอร์ม)
            </button>
            <button
              role="tab"
              type="button"
              aria-selected={docSubTab === "scan"}
              className={`hv4-doc-tab ${
                docSubTab === "scan" ? "hv4-doc-tab--active" : ""
              }`}
              onClick={() => {
                setDocSubTab("scan");
                setMessage("");
              }}
            >
              เอกสารสแกน
            </button>
          </div>

          {/* SUBTAB 1.1: PRINT LIST (แบบฟอร์ม) */}
          {docSubTab === "print" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {printProblem && <AlertV4 tone="warn">{printProblem}</AlertV4>}
              {printBlockedReason && <AlertV4 tone="warn">{printBlockedReason}</AlertV4>}

              {/* Encounter row */}
              <div className="hv4-doc-enc-row">
                <label
                  htmlFor="pl_enc"
                  style={{ fontSize: "14px", fontWeight: 700 }}
                >
                  Encounter Number :
                </label>
                {printEncounters.length > 0 ? (
                  <div style={{ width: "320px" }}>
                    <SelectV4
                      id="pl_enc"
                      search
                      value={String(printTargetEncounterId ?? "")}
                      options={printEncounters.map((option) => ({
                        value: String(option.id),
                        text: option.text,
                      }))}
                      onChange={(value) => void handleSelectPrintEncounter(value)}
                    />
                  </div>
                ) : (
                  <input
                    id="pl_enc"
                    className="hv4-input"
                    style={{ width: "220px" }}
                    readOnly
                    value={encNumber}
                  />
                )}
                <span className="hv4-doc-enc-tip">
                  แบบฟอร์มจะใส่ข้อมูลผู้ป่วยและ Encounter ที่เลือกให้อัตโนมัติ
                </span>
              </div>

              {/* Two Column Layout: Forms Sidebar + PDF Viewer */}
              <div className="hv4-doc-layout">
                {/* Left: Form selector */}
                <section className="hv4-doc-sidebar">
                  <div>
                    <input
                      id="pl_q"
                      className="hv4-input"
                      style={{ width: "100%" }}
                      placeholder="ค้นหาชื่อเอกสาร"
                      value={formSearch}
                      onChange={(e) => setFormSearch(e.target.value)}
                    />
                  </div>
                  <div
                    className="hv4-doc-table-head"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>ชื่อเอกสาร</span>
                  </div>
                  <div className="hv4-doc-form-list">
                    {filteredForms.map((item) => {
                      const isSelected = selectedFormId === item.id;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          className={`hv4-doc-form-btn ${
                            isSelected ? "hv4-doc-form-btn--active" : ""
                          }`}
                          onClick={() => handleSelectForm(item)}
                        >
                          {item.name}
                        </button>
                      );
                    })}
                    {filteredForms.length === 0 && (
                      <div
                        style={{
                          padding: "32px 16px",
                          textAlign: "center",
                          color: "var(--hv4-ink-off)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "15px",
                            fontWeight: 700,
                            color: "var(--hv4-ink-title)",
                            marginBottom: "4px",
                          }}
                        >
                          ยังไม่มีเอกสารนี้
                        </div>
                        <div style={{ fontSize: "13px" }}>
                          {formSearch.trim()
                            ? "ไม่พบแบบฟอร์มที่ค้นหา"
                            : "ยังไม่มีรายการแบบฟอร์มคลินิกในระบบ"}
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                {/* Right: PDF Viewer */}
                <section className="hv4-doc-viewer">
                  <div className="hv4-doc-toolbar">
                    <span
                      aria-hidden="true"
                      style={{ fontSize: "18px", display: "inline-flex" }}
                    >
                      <Menu size={18} />
                    </span>
                    <span className="hv4-doc-filename" title={currentFormName}>
                      {currentFormName || "เลือกแบบฟอร์มเพื่อดูตัวอย่าง"}
                    </span>
                    <button
                      type="button"
                      disabled={!printFileUrl}
                      title="ดาวน์โหลด PDF"
                      onClick={() => downloadPrintFile(printFileUrl, currentFormName || "แบบฟอร์ม")}
                      style={{
                        minWidth: "44px",
                        minHeight: "44px",
                        border: 0,
                        background: "transparent",
                        color: "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "14px",
                        cursor: printFileUrl ? "pointer" : "not-allowed",
                        opacity: printFileUrl ? 1 : 0.5,
                      }}
                    >
                      <Download size={16} /> ดาวน์โหลด
                    </button>
                  </div>

                  <div className="hv4-doc-canvas">
                    {printFileUrl ? (
                      <iframe
                        title={`ตัวอย่าง ${currentFormName}`}
                        src={printFileUrl}
                        style={{ width: "100%", minHeight: "640px", border: 0, background: "#fff" }}
                      />
                    ) : (
                      <div
                        style={{
                          padding: "64px 24px",
                          textAlign: "center",
                          color: "#6B7C83",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "18px",
                            fontWeight: 700,
                            color: "#243036",
                            marginBottom: "8px",
                          }}
                        >
                          {formsSource.length === 0
                            ? "ยังไม่มีเอกสารนี้"
                            : isPreparingForm || printTask
                              ? "กำลังเตรียมเอกสาร…"
                              : "ยังไม่มีเอกสารสำหรับแสดง"}
                        </div>
                        <div style={{ fontSize: "14px" }}>
                          {formsSource.length === 0
                            ? "ยังไม่มีแบบฟอร์มคลินิกในระบบสำหรับแสดงตัวอย่าง"
                            : printTask
                              ? `เอกสารนี้ใช้เวลาสร้างนาน${printTask.progress ? ` (${printTask.progress})` : ""}`
                              : isPreparingForm
                                ? "กรุณารอสักครู่"
                                : "เลือกแบบฟอร์มจากรายการด้านซ้าย"}
                        </div>
                        {printTask && (
                          <div style={{ marginTop: "12px" }}>
                            <ButtonV4 variant="ghost" size="sm" onClick={handleCancelPrintTask}>
                              ยกเลิกการสร้างเอกสาร
                            </ButtonV4>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </section>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <ButtonV4
                  variant="primary"
                  size="md"
                  style={{ minWidth: "280px", minHeight: "48px" }}
                  disabled={
                    !currentForm ||
                    !printContext ||
                    isPrintingForm ||
                    isPreparingForm ||
                    printTask !== null
                  }
                  onClick={() => void handlePrintForm()}
                >
                  <Printer size={18} /> {isPrintingForm ? "กำลังสั่งพิมพ์…" : "พิมพ์"}
                </ButtonV4>
              </div>
            </div>
          )}

          {/* SUBTAB 1.2: SCANNED DOCUMENTS (เอกสารสแกน) */}
          {docSubTab === "scan" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {fileProblem && <AlertV4 tone="warn">{fileProblem}</AlertV4>}

              {/* Card 1: Scanner Upload Section */}
              <section className="hv4-card">
                <h2 className="hv4-card-title">สแกนเอกสารเก็บเข้าประวัติ</h2>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "12px",
                    alignItems: "end",
                  }}
                >
                  <div>
                    <label className="hv4-label" htmlFor="sc_t">
                      ประเภทเอกสาร *
                    </label>
                    <SelectV4
                      id="sc_t"
                      search
                      placeholder="เลือกประเภทเอกสาร"
                      disabled={retryDoc !== null}
                      value={scanTypeId}
                      options={typeOptions}
                      onChange={(val) => setScanTypeId(val)}
                    />
                  </div>
                  <div>
                    <label className="hv4-label" htmlFor="sc_n">
                      ชื่อเอกสาร (ไม่กรอกระบบตั้งให้)
                    </label>
                    <input
                      id="sc_n"
                      className="hv4-input"
                      placeholder="เช่น ผลตรวจจาก รพ. อื่น"
                      maxLength={30}
                      disabled={retryDoc !== null}
                      value={scanNote}
                      onChange={(e) => setScanNote(e.target.value)}
                    />
                  </div>
                </div>

                <div
                  style={{
                    marginTop: "12px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  <input
                    ref={fileInputRef}
                    id="sc_files"
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.pdf,image/jpeg,application/pdf"
                    style={{ display: "none" }}
                    onChange={handleNativeFileUpload}
                  />
                  <ButtonV4
                    variant="primary"
                    size="sm"
                    disabled={isSavingDoc || isReadingFiles || retryDoc !== null}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Paperclip size={16} />{" "}
                    {isReadingFiles ? "กำลังอ่านไฟล์…" : "สแกน / เลือกไฟล์"}
                  </ButtonV4>
                </div>

                {stagedFiles.length > 0 && (
                  <div className="hv4-doc-staged">
                    <b style={{ fontSize: "15px" }}>
                      {retryDoc !== null
                        ? `หน้าที่ยังบันทึกไม่สำเร็จ (${stagedFiles.length} หน้า)`
                        : `ไฟล์ที่เลือกไว้ (${stagedFiles.length} หน้า)`}
                    </b>
                    <div
                      style={{
                        marginTop: "8px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      {stagedFiles.map((page, i) => (
                        <div key={`${page.name}-${i}`} className="hv4-doc-staged-item">
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              minWidth: 0,
                              overflowWrap: "anywhere",
                            }}
                          >
                            <img
                              src={`data:image/jpeg;base64,${page.image}`}
                              alt=""
                              style={{ width: "40px", height: "40px", objectFit: "cover" }}
                            />
                            {page.name}
                          </span>
                          {retryDoc === null && (
                            <ButtonV4
                              variant="ghost"
                              size="sm"
                              aria-label={`ลบไฟล์ ${page.name}`}
                              disabled={isSavingDoc}
                              onClick={() => handleRemoveStaged(i)}
                            >
                              ลบ
                            </ButtonV4>
                          )}
                        </div>
                      ))}
                    </div>
                    <div
                      style={{
                        marginTop: "10px",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                      }}
                    >
                      <ButtonV4
                        variant="ok"
                        size="sm"
                        disabled={isSavingDoc || !scanTypeId}
                        onClick={handleSaveScannedDoc}
                      >
                        {isSavingDoc
                          ? "กำลังบันทึก…"
                          : retryDoc !== null
                            ? `ส่งหน้าที่เหลืออีกครั้ง (${stagedFiles.length} หน้า)`
                            : `บันทึกเป็น 1 เอกสาร (${stagedFiles.length} หน้า)`}
                      </ButtonV4>
                      <ButtonV4
                        variant="ghost"
                        size="sm"
                        disabled={isSavingDoc}
                        onClick={handleClearStaged}
                      >
                        ล้างรายการ
                      </ButtonV4>
                    </div>
                    {!scanTypeId && (
                      <div
                        style={{
                          marginTop: "6px",
                          fontSize: "13px",
                          color: "var(--hv4-danger-text)",
                        }}
                      >
                        กรุณาเลือกประเภทเอกสารก่อนบันทึก
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* Card 2: Scanned Documents List & Preview */}
              <section className="hv4-card">
                <h2 className="hv4-card-title">เอกสารที่สแกนไว้</h2>

                <div
                  style={{
                    marginTop: "12px",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "12px",
                    alignItems: "end",
                  }}
                >
                  <div>
                    <label className="hv4-label" htmlFor="sf_c">
                      หมวดเอกสาร
                    </label>
                    <SelectV4
                      id="sf_c"
                      search
                      value={filterForm.categoryId}
                      options={[{ value: "all", text: "ทั้งหมด" }, ...categoryOptions]}
                      onChange={(val) => updateFilter({ categoryId: val, typeId: "all" })}
                    />
                  </div>
                  <div>
                    <label className="hv4-label" htmlFor="dtype">
                      ประเภทเอกสาร
                    </label>
                    <SelectV4
                      id="dtype"
                      search
                      value={filterForm.typeId}
                      options={[{ value: "all", text: "ทั้งหมด" }, ...filterTypeOptions]}
                      onChange={(val) => updateFilter({ typeId: val })}
                    />
                  </div>
                  <div>
                    <label className="hv4-label" htmlFor="sf_q">
                      ชื่อ / เลขที่เอกสาร
                    </label>
                    <input
                      id="sf_q"
                      className="hv4-input"
                      placeholder="พิมพ์ชื่อหรือเลขที่เอกสาร"
                      value={filterForm.query}
                      onChange={(e) => updateFilter({ query: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="hv4-label" htmlFor="sf_a">
                      สแกนตั้งแต่วันที่
                    </label>
                    <input
                      id="sf_a"
                      className="hv4-input"
                      placeholder="วว/ดด/ปปปป (พ.ศ.)"
                      value={filterForm.scanFrom}
                      onChange={(e) => updateFilter({ scanFrom: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="hv4-label" htmlFor="sf_b">
                      สแกนถึงวันที่
                    </label>
                    <input
                      id="sf_b"
                      className="hv4-input"
                      placeholder="วว/ดด/ปปปป (พ.ศ.)"
                      value={filterForm.scanTo}
                      onChange={(e) => updateFilter({ scanTo: e.target.value })}
                    />
                  </div>
                </div>

                {showMoreFilters && (
                  <div
                    style={{
                      marginTop: "12px",
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: "12px",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <label className="hv4-label" htmlFor="sf_df">
                        วันที่เอกสารตั้งแต่
                      </label>
                      <input
                        id="sf_df"
                        className="hv4-input"
                        placeholder="วว/ดด/ปปปป (พ.ศ.)"
                        value={filterForm.docFrom}
                        onChange={(e) => updateFilter({ docFrom: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="hv4-label" htmlFor="sf_dt">
                        วันที่เอกสารถึง
                      </label>
                      <input
                        id="sf_dt"
                        className="hv4-input"
                        placeholder="วว/ดด/ปปปป (พ.ศ.)"
                        value={filterForm.docTo}
                        onChange={(e) => updateFilter({ docTo: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="hv4-label" htmlFor="sf_ef">
                        วันหมดอายุตั้งแต่
                      </label>
                      <input
                        id="sf_ef"
                        className="hv4-input"
                        placeholder="วว/ดด/ปปปป (พ.ศ.)"
                        value={filterForm.expiryFrom}
                        onChange={(e) => updateFilter({ expiryFrom: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="hv4-label" htmlFor="sf_et">
                        วันหมดอายุถึง
                      </label>
                      <input
                        id="sf_et"
                        className="hv4-input"
                        placeholder="วว/ดด/ปปปป (พ.ศ.)"
                        value={filterForm.expiryTo}
                        onChange={(e) => updateFilter({ expiryTo: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="hv4-label" htmlFor="sf_od">
                        แผนกเจ้าของเอกสาร
                      </label>
                      <SelectV4
                        id="sf_od"
                        search
                        value={filterForm.ownerDivisionId}
                        options={[{ value: "all", text: "ทั้งหมด" }, ...divisionSelectOptions]}
                        onChange={(val) => updateFilter({ ownerDivisionId: val })}
                      />
                    </div>
                    <div>
                      <label className="hv4-label" htmlFor="sf_sd">
                        แผนกที่สแกน
                      </label>
                      <SelectV4
                        id="sf_sd"
                        search
                        value={filterForm.scanDivisionId}
                        options={[{ value: "all", text: "ทั้งหมด" }, ...divisionSelectOptions]}
                        onChange={(val) => updateFilter({ scanDivisionId: val })}
                      />
                    </div>
                    <div>
                      <label className="hv4-label" htmlFor="sf_dr">
                        แพทย์
                      </label>
                      <SelectV4
                        id="sf_dr"
                        search
                        value={filterForm.doctorId}
                        options={[{ value: "all", text: "ทั้งหมด" }, ...doctorOptions]}
                        onChange={(val) => updateFilter({ doctorId: val })}
                      />
                    </div>
                  </div>
                )}

                <div
                  style={{
                    marginTop: "12px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px 16px",
                    alignItems: "center",
                  }}
                >
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      minHeight: "44px",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      id="sf_enc"
                      type="checkbox"
                      checked={filterForm.onlyEncounter}
                      onChange={(e) => updateFilter({ onlyEncounter: e.target.checked })}
                      style={{ width: "20px", height: "20px" }}
                    />
                    เฉพาะ Encounter นี้
                  </label>
                  <ButtonV4
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowMoreFilters((current) => !current)}
                  >
                    {showMoreFilters ? "ซ่อนตัวกรองเพิ่มเติม" : "ตัวกรองเพิ่มเติม"}
                  </ButtonV4>
                  <ButtonV4
                    variant="ghost"
                    size="sm"
                    onClick={() => updateFilter(EMPTY_SCAN_FILTER)}
                  >
                    ล้างตัวกรอง
                  </ButtonV4>
                  <span style={{ fontSize: "14px", color: "var(--hv4-ink-label)" }}>
                    พบ {filteredDocs.length} เอกสาร
                  </span>
                </div>

                {hasDateProblem && (
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: "13px",
                      color: "var(--hv4-danger-text)",
                    }}
                  >
                    รูปแบบวันที่: วว/ดด/ปปปป (พ.ศ.) เช่น 26/10/2569 ·
                    ตัวกรองวันที่ที่พิมพ์ผิดจะยังไม่ถูกใช้
                  </div>
                )}

                {/* Two-Pane Layout: Docs List + Single Doc Preview */}
                <div
                  style={{
                    marginTop: "10px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "16px",
                    alignItems: "flex-start",
                  }}
                >
                  {/* Left: Documents List */}
                  <div
                    style={{
                      flex: "1 1 340px",
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {visibleDocs.map((doc) => {
                      const isSelected = selectedDoc && selectedDoc.id === doc.id;
                      return (
                        <div
                          key={doc.id}
                          className={`hv4-doc-scan-item ${
                            isSelected ? "hv4-doc-scan-item--active" : ""
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedDocId(doc.id);
                              setViewFileIdx(0);
                            }}
                            style={{
                              display: "flex",
                              flex: 1,
                              gap: "12px",
                              alignItems: "center",
                              minHeight: "72px",
                              padding: "8px 12px",
                              border: 0,
                              background: "transparent",
                              textAlign: "left",
                              fontSize: "14px",
                              minWidth: 0,
                              cursor: "pointer",
                            }}
                          >
                            <span className="hv4-doc-thumb">
                              <FileText size={24} color="#5B6C73" />
                              <span className="hv4-doc-badge">{doc.files.length}</span>
                            </span>
                            <span
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "2px",
                                minWidth: 0,
                              }}
                            >
                              <b style={{ textAlign: "left", overflowWrap: "anywhere" }}>
                                {doc.name}
                              </b>
                              <span style={{ fontSize: "13px", color: "var(--hv4-ink-label)" }}>
                                {doc.type || "-"} · {doc.files.length} หน้า · {doc.at || "-"}
                              </span>
                              <span style={{ fontSize: "12px", color: "var(--hv4-ink-soft)" }}>
                                สแกนโดย {doc.by || "-"}
                              </span>
                            </span>
                          </button>

                          <span style={{ display: "flex", gap: "6px", paddingRight: "8px" }}>
                            <ButtonV4
                              variant="ghost"
                              size="sm"
                              disabled={isDeletingDoc}
                              aria-label={`ยกเลิกเอกสาร ${doc.name}`}
                              onClick={() => setCancelTarget(doc)}
                            >
                              ยกเลิก
                            </ButtonV4>
                          </span>
                        </div>
                      );
                    })}

                    {filteredDocs.length > DOC_PAGE_SIZE && (
                      <PaginationV4
                        page={currentDocPage}
                        pageCount={docPageCount}
                        total={filteredDocs.length}
                        pageSize={DOC_PAGE_SIZE}
                        onChange={setDocPage}
                      />
                    )}

                    {filteredDocs.length === 0 && (
                      <div
                        style={{
                          padding: "36px 20px",
                          textAlign: "center",
                          color: "var(--hv4-ink-off)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: 700,
                            color: "var(--hv4-ink-title)",
                            marginBottom: "6px",
                          }}
                        >
                          ยังไม่มีเอกสารนี้
                        </div>
                        <div style={{ fontSize: "13px" }}>
                          {slice.scannedDocs.length > 0
                            ? "ไม่พบเอกสารสแกนตามเงื่อนไขที่ค้นหา"
                            : "ยังไม่มีเอกสารสแกนของผู้ป่วยรายนี้ในระบบ สามารถกด \"สแกน / เลือกไฟล์\" ด้านบนเพื่อเพิ่มเอกสาร"}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: File Previewer Pane */}
                  <div className="hv4-doc-preview-pane">
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "6px",
                      }}
                    >
                      <b style={{ fontSize: "14px", overflowWrap: "anywhere" }}>
                        {selectedDoc ? selectedDoc.name : "เลือกเอกสารเพื่อดู"}
                      </b>
                      {selectedDoc && (
                        <span
                          style={{
                            display: "inline-flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: "14px",
                          }}
                        >
                          <ButtonV4
                            variant="ghost"
                            size="sm"
                            aria-label="หน้าก่อนหน้า"
                            disabled={activeFileIdx <= 0}
                            onClick={() => setViewFileIdx(activeFileIdx - 1)}
                          >
                            ◀
                          </ButtonV4>
                          หน้า {selectedDoc.files.length === 0 ? 0 : activeFileIdx + 1} /{" "}
                          {selectedDoc.files.length}
                          <ButtonV4
                            variant="ghost"
                            size="sm"
                            aria-label="หน้าถัดไป"
                            disabled={activeFileIdx >= selectedDoc.files.length - 1}
                            onClick={() => setViewFileIdx(activeFileIdx + 1)}
                          >
                            ▶
                          </ButtonV4>
                          <ButtonV4
                            variant="ghost"
                            size="sm"
                            aria-label="ย่อ"
                            disabled={viewZoom <= 50}
                            onClick={() => setViewZoom(viewZoom - PDF_ZOOM_STEP)}
                          >
                            −
                          </ButtonV4>
                          {viewZoom}%
                          <ButtonV4
                            variant="ghost"
                            size="sm"
                            aria-label="ขยาย"
                            disabled={viewZoom >= 200}
                            onClick={() => setViewZoom(viewZoom + PDF_ZOOM_STEP)}
                          >
                            +
                          </ButtonV4>
                          <ButtonV4
                            variant="ghost"
                            size="sm"
                            aria-label="พิมพ์เอกสาร"
                            disabled={isPreparingOutput || selectedDoc.files.length === 0}
                            onClick={() => handleDocOutput("print")}
                          >
                            <Printer size={16} />
                          </ButtonV4>
                          <ButtonV4
                            variant="ghost"
                            size="sm"
                            aria-label="ดาวน์โหลดเอกสาร"
                            disabled={isPreparingOutput || selectedDoc.files.length === 0}
                            onClick={() => handleDocOutput("download")}
                          >
                            <Download size={16} />
                          </ButtonV4>
                        </span>
                      )}
                    </div>

                    <div className="hv4-doc-preview-box" style={{ overflow: "auto" }}>
                      {selectedDoc ? (
                        activeFile && failedImage !== activeFile ? (
                          <img
                            src={activeFile}
                            alt={`${selectedDoc.name} หน้า ${activeFileIdx + 1}`}
                            style={{ display: "block", width: `${viewZoom}%`, margin: "0 auto" }}
                            onError={() => setFailedImage(activeFile)}
                          />
                        ) : (
                          <div>
                            <FileText
                              size={44}
                              style={{ margin: "0 auto 8px", display: "block" }}
                            />
                            <b>{activeFile ? "เปิดภาพเอกสารไม่สำเร็จ" : "ไม่พบไฟล์"}</b>
                          </div>
                        )
                      ) : (
                        <div
                          style={{
                            padding: "36px 16px",
                            textAlign: "center",
                            color: "var(--hv4-ink-off)",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: 700,
                              color: "var(--hv4-ink-title)",
                              marginBottom: "6px",
                            }}
                          >
                            ยังไม่มีเอกสารนี้
                          </div>
                          <div style={{ fontSize: "13px" }}>
                            ยังไม่มีเอกสารสแกนที่เลือกสำหรับแสดงตัวอย่าง
                          </div>
                        </div>
                      )}
                    </div>

                    {selectedDoc && (
                      <div
                        style={{
                          marginTop: "10px",
                          fontSize: "13px",
                          color: "var(--hv4-ink-soft)",
                        }}
                      >
                        {selectedDoc.type || "-"} · สแกน {selectedDoc.at || "-"} โดย{" "}
                        {selectedDoc.by || "-"} · วันที่เอกสาร {selectedDoc.documentDate || "-"} ·
                        หมดอายุ {selectedDoc.expiryDate || "-"} · HN {patientHn || "-"} ·{" "}
                        {selectedDoc.files.length} หน้า
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* ยกเลิกเอกสาร: BE ตรวจ username/password และเหตุผลทุกครั้ง */}
              {cancelTarget && (
                <ModalV4
                  title={`ยกเลิกเอกสาร · ${cancelTarget.name}`}
                  width={480}
                  onClose={closeCancelModal}
                >
                  {apiError && <AlertV4 tone="danger">{apiError}</AlertV4>}
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <label className="hv4-label" htmlFor="cd_user">
                      ชื่อผู้ใช้ *
                    </label>
                    <input
                      id="cd_user"
                      className="hv4-input"
                      autoComplete="off"
                      value={cancelUsername}
                      onChange={(e) => setCancelUsername(e.target.value)}
                    />
                    <label className="hv4-label" htmlFor="cd_pass">
                      รหัสผ่าน *
                    </label>
                    <input
                      id="cd_pass"
                      type="password"
                      className="hv4-input"
                      autoComplete="off"
                      value={cancelPassword}
                      onChange={(e) => setCancelPassword(e.target.value)}
                    />
                    <label className="hv4-label" htmlFor="cd_note">
                      เหตุผลในการยกเลิก *
                    </label>
                    <textarea
                      id="cd_note"
                      className="hv4-input"
                      rows={2}
                      value={cancelNote}
                      onChange={(e) => setCancelNote(e.target.value)}
                      style={{ width: "100%", resize: "vertical" }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "8px",
                      marginTop: "16px",
                    }}
                  >
                    <ButtonV4 variant="ghost" size="md" onClick={closeCancelModal}>
                      ปิด
                    </ButtonV4>
                    <ButtonV4
                      variant="danger-ghost"
                      size="md"
                      disabled={
                        isDeletingDoc ||
                        !cancelUsername.trim() ||
                        !cancelPassword ||
                        !cancelNote.trim()
                      }
                      onClick={handleCancelDoc}
                    >
                      {isDeletingDoc ? "กำลังยกเลิก…" : "ยืนยันยกเลิกเอกสาร"}
                    </ButtonV4>
                  </div>
                </ModalV4>
              )}
            </div>
          )}
        </>
      )}

      {/* ========================================================================= */}
      {/* MODE 2: MEDICAL CERTIFICATE SCREEN (ใบรับรองแพทย์) */}
      {/* ========================================================================= */}
      {docMode === "medcert" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Card: Issue Medical Certificate */}
          <section id="sec-mc" className="hv4-card">
            <h2 className="hv4-card-title">ใบรับรองแพทย์</h2>
            {certPrintProblem && <AlertV4 tone="warn">{certPrintProblem}</AlertV4>}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "12px",
                alignItems: "end",
              }}
            >
              <div>
                <label
                  htmlFor="mc_t"
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    marginBottom: "4px",
                  }}
                >
                  ประเภทใบรับรอง
                </label>
                <SelectV4
                  id="mc_t"
                  placeholder="ไม่มีชนิดที่ออกจากหน้านี้ได้"
                  value={selectedCertTypeCode}
                  options={issuableCertTypes.map((type) => ({
                    value: type.code,
                    text: type.name,
                  }))}
                  onChange={(val) => setCertTypeCode(val)}
                />
              </div>

              <div>
                <label
                  htmlFor="mc_f"
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    marginBottom: "4px",
                  }}
                >
                  ใช้เพื่อ / ยื่นต่อ
                </label>
                <input
                  id="mc_f"
                  className="hv4-input"
                  placeholder="เช่น นายจ้าง สถานศึกษา บริษัทประกัน"
                  value={certFor}
                  onChange={(e) => setCertFor(e.target.value)}
                />
              </div>

              <div>
                <label
                  htmlFor="mc_d"
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    marginBottom: "4px",
                  }}
                >
                  ผลการวินิจฉัย
                </label>
                <input
                  id="mc_d"
                  className="hv4-input"
                  style={{ background: "#EEF3F4" }}
                  readOnly
                  value={certDxDisplay}
                />
              </div>
            </div>

            {/* Rest recommendation checkbox */}
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                flexWrap: "wrap",
                gap: "8px 20px",
                alignItems: "center",
              }}
            >
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  minHeight: "44px",
                  fontSize: "15px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={certRest}
                  onChange={(e) => setCertRest(e.target.checked)}
                  style={{ width: "20px", height: "20px" }}
                />
                แนะนำให้พักรักษาตัว
              </label>

              {certRest && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                  }}
                >
                  ตั้งแต่{" "}
                  <input
                    className="hv4-input"
                    aria-label="วันที่เริ่มพัก"
                    style={{ width: "140px" }}
                    value={certFromDate}
                    onChange={(e) => setCertFromDate(e.target.value)}
                  />{" "}
                  จำนวน{" "}
                  <input
                    className="hv4-input"
                    aria-label="จำนวนวันพัก"
                    style={{ width: "80px" }}
                    value={certDays}
                    onChange={(e) => setCertDays(e.target.value)}
                  />{" "}
                  วัน
                </span>
              )}
            </div>

            {/* Doctor opinion textarea */}
            <div style={{ marginTop: "8px" }}>
              <label
                htmlFor="mc_o"
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 600,
                  marginBottom: "4px",
                }}
              >
                ความเห็นแพทย์
              </label>
              <textarea
                id="mc_o"
                className="hv4-input"
                rows={2}
                placeholder="เช่น ไม่ควรยกของหนักและนั่งนานติดต่อกัน"
                value={certOpinion}
                onChange={(e) => setCertOpinion(e.target.value)}
                style={{ width: "100%", resize: "vertical" }}
              />
            </div>

            {/* Issue button */}
            <div style={{ marginTop: "12px" }}>
              <ButtonV4
                variant="primary"
                size="md"
                disabled={isIssuingCert || certProblem !== null}
                onClick={handleIssueCert}
              >
                {isIssuingCert ? "กำลังออกใบรับรอง…" : "ออกใบรับรองแพทย์"}
              </ButtonV4>
              {certProblem !== null && (
                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "13px",
                    color: "var(--hv4-danger-text)",
                  }}
                >
                  {certProblem}
                </div>
              )}
            </div>

            {/* History of Issued Medical Certificates */}
            <div
              style={{
                marginTop: "16px",
                paddingTop: "14px",
                borderTop: "1px solid var(--hv4-line)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <b
                  style={{
                    fontSize: "14px",
                    color: "var(--hv4-ink-label)",
                  }}
                >
                  ประวัติใบรับรองแพทย์ที่เคยออก
                </b>

              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {slice.medicalCerts.map((cert) => (
                  <button
                    key={cert.id}
                    type="button"
                    className="hv4-doc-cert-card"
                    title={`เปิดดูข้อมูลเลขที่ ${cert.no || "-"}`}
                    onClick={() => handleOpenCert(cert)}
                    style={{ position: "relative" }}
                  >

                    <FileText size={28} color="#1B7FCC" />
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "var(--hv4-ink)",
                      }}
                    >
                      {cert.no ? `เลขที่ ${cert.no}` : "-"}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "var(--hv4-ink-soft)",
                      }}
                    >
                      {cert.day}
                    </span>
                  </button>
                ))}
                {slice.medicalCerts.length === 0 && (
                  <div
                    style={{
                      width: "100%",
                      padding: "36px 20px",
                      textAlign: "center",
                      color: "var(--hv4-ink-off)",
                      background: "var(--hv4-canvas-soft)",
                      borderRadius: "6px",
                      border: "1px dashed var(--hv4-line-subtle)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "var(--hv4-ink-title)",
                        marginBottom: "6px",
                      }}
                    >
                      ยังไม่มีเอกสารนี้
                    </div>
                    <div style={{ fontSize: "13px" }}>
                      ยังไม่มีประวัติการออกใบรับรองแพทย์ของผู้ป่วยรายนี้ สามารถกรอกข้อมูลด้านบนแล้วกด "ออกใบรับรองแพทย์" เพื่อสร้างเอกสารใหม่
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Medical Certificate Full Preview Modal */}
          {previewCert && (
            <ModalV4
              title={`ใบรับรองแพทย์ · เลขที่ ${previewCert.no || "-"}`}
              width={680}
              onClose={() => setPreviewCert(null)}
            >
              {apiError && <AlertV4 tone="danger">{apiError}</AlertV4>}
              {certPrintProblem && <AlertV4 tone="warn">{certPrintProblem}</AlertV4>}
              {previewCert && !isPrintableCertCode(previewCert.formCode) && (
                <AlertV4 tone="warn">ใบรับรองชนิดนี้ต้องพิมพ์จากเมนู V3</AlertV4>
              )}

              <div className="hv4-doc-cert-doc">
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "22px",
                    fontWeight: 700,
                    marginBottom: "4px",
                  }}
                >
                  ใบรับรองแพทย์
                </div>
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "13px",
                    color: "var(--hv4-ink-soft)",
                    marginBottom: "18px",
                  }}
                >
                  {previewCert.type} · เลขที่ {previewCert.no || "-"}
                  {previewDetail && (isCertCopy(previewData) ? " · สำเนา" : " · ต้นฉบับ")}
                </div>

                {previewDetail === null ? (
                  <p>{isLoadingCert ? "กำลังโหลด…" : "ยังไม่มีข้อมูลของใบรับรองนี้"}</p>
                ) : (
                  <>
                    <div
                      style={{
                        textAlign: "right",
                        fontSize: "13px",
                        marginBottom: "10px",
                      }}
                    >
                      วันที่ {previewData.createdDate || previewCert.day || "-"}
                    </div>
                    <p style={{ margin: "0 0 8px" }}>
                      ข้าพเจ้า <b>{previewData.doctorName || previewCert.doctor || "-"}</b>{" "}
                      ใบอนุญาตประกอบวิชาชีพเวชกรรมเลขที่ {previewData.doctorCertNumber || "-"}{" "}
                      ได้ตรวจร่างกาย <b>{previewData.patientInfo?.full_name || "-"}</b> (HN{" "}
                      {previewData.patientInfo?.hn || "-"}) เมื่อวันที่{" "}
                      {previewData.examDate || "-"}
                    </p>
                    <p style={{ margin: "0 0 8px", paddingLeft: "16px" }}>
                      อาการสำคัญ : {previewData.chiefComplaint || "-"}
                    </p>
                    <p style={{ margin: "0 0 8px", paddingLeft: "16px" }}>
                      การวินิจฉัย : <b>{previewData.diagnosis || "-"}</b>
                    </p>
                    <p style={{ margin: "0 0 8px", paddingLeft: "16px", whiteSpace: "pre-wrap" }}>
                      ความเห็นแพทย์ : {previewData.comment || "-"}
                    </p>
                    <p style={{ margin: 0, paddingLeft: "16px" }}>
                      ระยะเวลาที่ควรพัก :{" "}
                      {previewData.patientAbsenceStart
                        ? `${previewData.dayDuration || "-"} วัน ตั้งแต่ ${previewData.patientAbsenceStart} ถึง ${previewData.patientAbsenceEnd || "-"}`
                        : "-"}
                    </p>
                  </>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                  marginTop: "16px",
                  paddingTop: "12px",
                  borderTop: "1px solid var(--hv4-line)",
                }}
              >
                {isPrintableCertCode(previewCert.formCode) && (
                  <ButtonV4
                    variant="primary"
                    size="md"
                    disabled={previewDetail === null || isReprintingCert}
                    onClick={handleReprintCert}
                  >
                    <Printer size={16} /> {isReprintingCert ? "กำลังเตรียมพิมพ์…" : "พิมพ์สำเนา"}
                  </ButtonV4>
                )}
                <ButtonV4
                  variant="ghost"
                  size="md"
                  onClick={() => setPreviewCert(null)}
                >
                  ปิด
                </ButtonV4>
              </div>
            </ModalV4>
          )}
        </div>
      )}
    </>
  );
};

export default CardDocumentV4;
